// Validation rules for the SARS Power of Attorney.
// validateTaxNumber, validateSAIdNumber and validateLuhnChecksum are copied verbatim from
// Personal-Tax-Services-Agreement/frontend/src/pages/PageOne.jsx so both sites accept and
// reject exactly the same numbers. Keep them in sync if the tax site's rules change.

export const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

export const validateTaxNumber = (taxNumber) => {
  if (!taxNumber || taxNumber.trim() === '') {
    return { valid: false, error: '' }; // Empty handled by required field
  }

  // Remove any spaces
  const cleanTax = taxNumber.replace(/\s/g, '');

  // Check: Must be exactly 10 digits, no special characters, no letters
  if (!/^\d{10}$/.test(cleanTax)) {
    if (/[a-zA-Z]/.test(taxNumber)) {
      return { 
        valid: false, 
        error: 'Tax number must not contain letters'
      };
    }
    if (/[^0-9\s]/.test(taxNumber)) {
      return { 
        valid: false, 
        error: 'Tax number must not contain special characters'
      };
    }
    if (cleanTax.length !== 10) {
      return { 
        valid: false, 
        error: `Tax number must be exactly 10 digits. Currently ${cleanTax.length} digits.`
      };
    }
  }

  // Check: Tax number cannot start with 4, 7, or 9
  const firstDigit = cleanTax[0];
  if (firstDigit === '4' || firstDigit === '7' || firstDigit === '9') {
    return { 
      valid: false, 
      error: 'Invalid tax number, please double check'
    };
  }

  // Check: Prevent all same digits (e.g., 1111111111, 0000000000)
  if (/^(\d)\1{9}$/.test(cleanTax)) {
    return { 
      valid: false, 
      error: 'Invalid tax number - cannot be all the same digit'
    };
  }

  // Check: Prevent sequential ascending numbers (e.g., 0123456789, 1234567890)
  const ascendingSequences = ['0123456789', '1234567890', '2345678901', '3456789012', '4567890123', '5678901234', '6789012345', '7890123456', '8901234567', '9012345678'];
  if (ascendingSequences.includes(cleanTax)) {
    return { 
      valid: false, 
      error: 'Invalid tax number - sequential numbers not allowed'
    };
  }

  // Check: Prevent sequential descending numbers (e.g., 9876543210, 0987654321)
  const descendingSequences = ['9876543210', '0987654321', '1098765432', '2109876543', '3210987654', '4321098765', '5432109876', '6543210987', '7654321098', '8765432109'];
  if (descendingSequences.includes(cleanTax)) {
    return { 
      valid: false, 
      error: 'Invalid tax number - sequential numbers not allowed'
    };
  }

  // Check: Prevent repeating patterns (e.g., 1212121212, 1231231231, 1234123412)
  // 2-digit repeating pattern
  if (/^(\d{2})\1{4}$/.test(cleanTax)) {
    return { 
      valid: false, 
      error: 'Invalid tax number - repeating pattern not allowed'
    };
  }
  // 5-digit repeating pattern
  if (/^(\d{5})\1$/.test(cleanTax)) {
    return { 
      valid: false, 
      error: 'Invalid tax number - repeating pattern not allowed'
    };
  }

  // Check: Prevent common bypass attempts
  const invalidNumbers = [
    '1234567890', '0987654321', '1111111111', '0000000000',
    '1234512345', '9999999999', '1010101010', '1122334455',
    '1212121212', '1231231231', '1234123412', '5555555555',
    '1357924680', '2468013579', '9876509876', '0123401234'
  ];
  if (invalidNumbers.includes(cleanTax)) {
    return { 
      valid: false, 
      error: 'Invalid tax number, please enter your actual tax number'
    };
  }

  // Check: Prevent any 2-digit pattern repeating 3 or more times
  const twoDigitPatterns = {};
  for (let i = 0; i <= cleanTax.length - 2; i++) {
    const pattern = cleanTax.substring(i, i + 2);
    twoDigitPatterns[pattern] = (twoDigitPatterns[pattern] || 0) + 1;
    
    if (twoDigitPatterns[pattern] >= 3) {
      return { 
        valid: false, 
        error: 'Invalid tax number - suspicious repeating pattern detected'
      };
    }
  }

  // Check: Prevent any 3-digit pattern repeating 3 or more times
  const threeDigitPatterns = {};
  for (let i = 0; i <= cleanTax.length - 3; i++) {
    const pattern = cleanTax.substring(i, i + 3);
    threeDigitPatterns[pattern] = (threeDigitPatterns[pattern] || 0) + 1;
    
    if (threeDigitPatterns[pattern] >= 3) {
      return { 
        valid: false, 
        error: 'Invalid tax number - suspicious repeating pattern detected'
      };
    }
  }

  // Check: Prevent any 4-digit pattern repeating 2 or more times
  const fourDigitPatterns = {};
  for (let i = 0; i <= cleanTax.length - 4; i++) {
    const pattern = cleanTax.substring(i, i + 4);
    fourDigitPatterns[pattern] = (fourDigitPatterns[pattern] || 0) + 1;
    
    if (fourDigitPatterns[pattern] >= 2) {
      return { 
        valid: false, 
        error: 'Invalid tax number - suspicious repeating pattern detected'
      };
    }
  }

  // Check: Prevent any 5-digit pattern repeating 2 or more times
  const fiveDigitPatterns = {};
  for (let i = 0; i <= cleanTax.length - 5; i++) {
    const pattern = cleanTax.substring(i, i + 5);
    fiveDigitPatterns[pattern] = (fiveDigitPatterns[pattern] || 0) + 1;
    
    if (fiveDigitPatterns[pattern] >= 2) {
      return { 
        valid: false, 
        error: 'Invalid tax number - suspicious repeating pattern detected'
      };
    }
  }

  // Check: Prevent 4+ consecutive ascending or descending digits
  for (let i = 0; i <= cleanTax.length - 4; i++) {
    let isAscending = true;
    let isDescending = true;
    
    // Check if next 4 digits are consecutive ascending
    for (let j = i; j < i + 3; j++) {
      const current = parseInt(cleanTax[j]);
      const next = parseInt(cleanTax[j + 1]);
      
      if (next !== current + 1) {
        isAscending = false;
      }
      if (next !== current - 1) {
        isDescending = false;
      }
    }
    
    if (isAscending || isDescending) {
      return { 
        valid: false, 
        error: 'Invalid tax number - sequential numbers not allowed'
      };
    }
  }

  return { valid: true, error: '' };
};

export const validateSAIdNumber = (idNumber) => {
  if (!idNumber || idNumber.trim() === '') {
    return { valid: false, error: '', info: null }; // Empty handled by required field
  }

  // Remove any spaces or dashes
  const cleanId = idNumber.replace(/[\s-]/g, '');

  // Check 1: Must be exactly 13 digits
  if (!/^\d{13}$/.test(cleanId)) {
    return { 
      valid: false, 
      error: 'ID number must be exactly 13 digits (no letters or special characters)',
      info: null 
    };
  }

  // Extract components
  const year = cleanId.substring(0, 2);
  const month = cleanId.substring(2, 4);
  const day = cleanId.substring(4, 6);
  const genderDigits = cleanId.substring(6, 10);
  const citizenshipDigit = cleanId.substring(10, 11);
  const raceDigit = cleanId.substring(11, 12);
  const checkDigit = cleanId.substring(12, 13);

  // Check 2: Validate date of birth (YYMMDD)
  const monthNum = parseInt(month, 10);
  const dayNum = parseInt(day, 10);
  const yearNum = parseInt(year, 10);

  // Month must be 01-12
  if (monthNum < 1 || monthNum > 12) {
    return { 
      valid: false, 
      error: `Invalid month in ID number: ${month}. Month must be between 01 and 12`,
      info: null 
    };
  }

  // Determine full year (assuming 00-current year+10 is 2000s, rest is 1900s)
  const currentYear = new Date().getFullYear();
  const currentYearLastTwo = currentYear % 100;
  const fullYear = yearNum <= currentYearLastTwo + 10 ? 2000 + yearNum : 1900 + yearNum;

  // Check if it's a leap year
  const isLeapYear = (fullYear % 4 === 0 && fullYear % 100 !== 0) || (fullYear % 400 === 0);

  // Days per month
  const daysInMonth = [31, isLeapYear ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  const maxDays = daysInMonth[monthNum - 1];

  if (dayNum < 1 || dayNum > maxDays) {
    return { 
      valid: false, 
      error: `Invalid day in ID number: ${day}. Day must be between 01 and ${maxDays} for month ${month}`,
      info: null 
    };
  }

  // Check if birth date is not in the future
  const birthDate = new Date(fullYear, monthNum - 1, dayNum);
  const today = new Date();
  if (birthDate > today) {
    return { 
      valid: false, 
      error: 'Date of birth cannot be in the future',
      info: null 
    };
  }

  // Check 3: Gender sequence (0000-4999 = Female, 5000-9999 = Male)
  const genderNum = parseInt(genderDigits, 10);
  const gender = genderNum < 5000 ? 'Female' : 'Male';

  // Check 4: Citizenship digit (0 = SA Citizen, 1 = Permanent Resident, 2 = Refugee)
  let citizenship = '';
  if (citizenshipDigit === '0') {
    citizenship = 'South African Citizen';
  } else if (citizenshipDigit === '1') {
    citizenship = 'Permanent Resident';
  } else if (citizenshipDigit === '2') {
    citizenship = 'Refugee';
  } else {
    return { 
      valid: false, 
      error: `Invalid citizenship digit: ${citizenshipDigit}. Must be 0 (SA Citizen), 1 (Permanent Resident), or 2 (Refugee)`,
      info: null 
    };
  }

  // Check 5: Race/obsolete digit (should be 8 or 9 in modern IDs)
  // Note: We don't reject based on this as older IDs may have different values
  const isModernId = raceDigit === '8' || raceDigit === '9';

  // Check 6: Luhn Algorithm (Modulus 10) for check digit validation
  const luhnValid = validateLuhnChecksum(cleanId);
  if (!luhnValid) {
    return { 
      valid: false, 
      error: 'Invalid ID number: Check digit validation failed',
      info: null 
    };
  }

  // Calculate age
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }

  // All validations passed
  return {
    valid: true,
    error: '',
    info: {
      dateOfBirth: birthDate.toLocaleDateString('en-ZA', { day: 'numeric', month: 'long', year: 'numeric' }),
      age: age,
      gender: gender,
      citizenship: citizenship,
      isModernId: isModernId
    }
  };
};

// Luhn Algorithm implementation
export const validateLuhnChecksum = (idNumber) => {
  let sum = 0;
  let isSecond = false;

  // Process from right to left
  for (let i = idNumber.length - 1; i >= 0; i--) {
    let digit = parseInt(idNumber[i], 10);

    if (isSecond) {
      digit *= 2;
      if (digit > 9) {
        digit = Math.floor(digit / 10) + (digit % 10);
      }
    }

    sum += digit;
    isSecond = !isSecond;
  }

  return sum % 10 === 0;
};

// Standalone POA only: the ID field accepts an SA ID number (full checks above) or a
// passport number. Anything containing a letter is treated as a passport number.
export const validateIdOrPassport = (value) => {
  const v = (value || '').trim();
  if (!v) return { valid: false, error: '', type: null };
  if (/[a-zA-Z]/.test(v)) {
    const clean = v.replace(/[\s-]/g, '');
    if (!/^[A-Za-z0-9]{6,15}$/.test(clean)) {
      return { valid: false, error: 'Passport number must be 6-15 letters and/or digits (no special characters)', type: 'passport' };
    }
    return { valid: true, error: '', type: 'passport' };
  }
  const r = validateSAIdNumber(v);
  return { valid: r.valid, error: r.error, type: 'sa_id' };
};

// Full name(s) and surname: letters, spaces, hyphens and apostrophes; at least two words.
export const validateFullName = (value) => {
  const v = (value || '').trim().replace(/\s+/g, ' ');
  if (!v) return '';
  if (!/^[A-Za-zÀ-ÖØ-öø-ÿ' -]+$/.test(v)) {
    return 'Name can only contain letters, spaces, hyphens and apostrophes';
  }
  if (v.split(' ').filter(Boolean).length < 2) {
    return 'Please enter your full name(s) and surname';
  }
  return '';
};

export const validateEmail = (email) => {
  if (!email || email.trim() === '') return '';
  if (!emailRegex.test(email.trim())) {
    return 'Please enter a valid email address (e.g., name@example.com)';
  }
  return '';
};

// ---------------------------------------------------------------------------------------
// Company POA (owner, 2026-10-27). Company income tax numbers start with 9, which the
// personal check above (a verbatim copy from the tax site) rejects - so companies get their
// own check: 10 digits starting with 9, plus the same fake-number checks as above.
// ---------------------------------------------------------------------------------------

// The fake-number checks of validateTaxNumber, for any 10-digit number
const fakeTaxNumberProblem = (clean) => {
  if (/^(\d)\1{9}$/.test(clean)) return 'Invalid tax number - cannot be all the same digit';
  if (/^(\d{2})\1{4}$/.test(clean) || /^(\d{5})\1$/.test(clean)) return 'Invalid tax number - repeating pattern not allowed';
  for (const [size, limit] of [[2, 3], [3, 3], [4, 2], [5, 2]]) {
    const seen = {};
    for (let i = 0; i <= clean.length - size; i++) {
      const p = clean.substring(i, i + size);
      seen[p] = (seen[p] || 0) + 1;
      if (seen[p] >= limit) return 'Invalid tax number - suspicious repeating pattern detected';
    }
  }
  for (let i = 0; i <= clean.length - 4; i++) {
    const d = clean.substring(i, i + 4).split('').map(Number);
    const up = d.every((v, j) => j === 0 || v === d[j - 1] + 1);
    const down = d.every((v, j) => j === 0 || v === d[j - 1] - 1);
    if (up || down) return 'Invalid tax number - sequential numbers not allowed';
  }
  return '';
};

export const validateCompanyTaxNumber = (taxNumber) => {
  if (!taxNumber || taxNumber.trim() === '') return { valid: false, error: '' };
  const clean = taxNumber.replace(/\s/g, '');
  if (/[a-zA-Z]/.test(taxNumber)) return { valid: false, error: 'Tax number must not contain letters' };
  if (/[^0-9\s]/.test(taxNumber)) return { valid: false, error: 'Tax number must not contain special characters' };
  if (clean.length !== 10) return { valid: false, error: `Tax number must be exactly 10 digits. Currently ${clean.length} digits.` };
  if (clean[0] !== '9') return { valid: false, error: 'A company income tax number starts with 9, please double check' };
  const fake = fakeTaxNumberProblem(clean);
  return fake ? { valid: false, error: fake } : { valid: true, error: '' };
};

// Registered company name, e.g. "Apex Solar and Electrical Distributors (Pty) Ltd"
export const validateCompanyName = (value) => {
  const v = (value || '').trim().replace(/\s+/g, ' ');
  if (!v) return '';
  if (!/^[A-Za-z0-9À-ÖØ-öø-ÿ&'().,\- ]+$/.test(v)) {
    return 'Company name can only contain letters, digits, spaces and & ( ) . , \' -';
  }
  if (v.length < 3) return 'Please enter the full registered company name';
  return '';
};
